import { ButtonLink } from '@app/components/base/ButtonLink';
import { Panel } from '@app/components/base/content-structure/Panel';
import { UserDisplay } from '@app/components/users';
import { ConnectionDisplayName } from '@app/modules/social-export/components/ConnectionDisplayName';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  SocialExportGetConnectionStateDto,
  socialExportGetConnectionByIdQueryKey,
} from '@mi-front/api-v2';
import { Settings } from '@mui/icons-material';
import { Alert, Avatar, Box, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connectionsManageMessages as messages } from './connections-manage-messages';
import { ConnectionStationChip } from './ConnectionStationChip';
import { PlatformDisconnect } from './PlatformDisconnect';
import { useConnectionStations } from './useConnectionStations';

type Props = {
  platform: SocialPlatform;
  connection: SocialExportGetConnectionStateDto;
};

/**
 * Detail panel for the selected connection: linked stations (read-only) and configure/disconnect.
 */
export function ConnectionDetailPanel({ platform, connection }: Props) {
  const queryClient = useQueryClient();
  const { profile } = connection;
  const { linkedStations, stationIds } = useConnectionStations(connection.stationIds);

  return (
    <Panel
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Avatar src={profile.photo || undefined} sx={{ width: 24, height: 24, flexShrink: 0 }}>
            {(connection.name || profile.displayName)?.charAt(0) || '?'}
          </Avatar>
          <ConnectionDisplayName connection={connection} component="span" variant="inherit" />
        </Box>
      }
      footer={
        <>
          <ButtonLink
            variant="outlined"
            color="info"
            size="small"
            to={`/admin/social-export/${platform.id.toLowerCase()}/settings/${connection.id}`}
            startIcon={<Settings />}
            onClick={() => {
              // Seed get-by-id cache so settings title shows the account name immediately.
              queryClient.setQueryData(
                socialExportGetConnectionByIdQueryKey(connection.id),
                connection,
              );
            }}
          >
            <FormattedMessage {...messages.configure} />
          </ButtonLink>
          <PlatformDisconnect
            connectionId={connection.id}
            platform={platform}
            linkedStationsCount={stationIds.length}
          />
        </>
      }
      sx={{ height: '100%', minHeight: 280 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          {linkedStations.length > 0 ? (
            <Typography variant="body2" sx={{ mb: 1.25 }}>
              <FormattedMessage {...messages.stationsPublishingWithAccount} />
            </Typography>
          ) : null}
          {linkedStations.length > 0 ? (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
              {linkedStations.map((station) => (
                <ConnectionStationChip key={station.id} station={station} />
              ))}
            </Stack>
          ) : (
            <Alert severity="warning" sx={{ py: 0.5, typography: 'body2', width: 'fit-content' }}>
              <FormattedMessage {...messages.noLinkedStations} />
            </Alert>
          )}
        </Box>

        {connection.connectedBy ? (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <FormattedMessage {...messages.connectedByPrefix} />
            <UserDisplay showIcon iconSize="small" id={connection.connectedBy} />
          </Typography>
        ) : null}
      </Box>
    </Panel>
  );
}
